import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Stack,
  TextField,
  Divider,
  Card,
  CardContent,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import {
  Upload as UploadIcon,
  Image as ImageIcon,
  TextFields as TextIcon,
  Inventory as ProductIcon,
  Save as SaveIcon,
  ArrowBack as BackIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  Delete as DeleteIcon,
  AutoFixHigh as AutoFixHighIcon,
} from '@mui/icons-material';
import { fabric } from 'fabric';
import toast from 'react-hot-toast';
import axios from 'axios';
import KamHeroHeader from '../../components/KamHeroHeader/KamHeroHeader';
import LocalGarmentPicker from '../../components/Design/LocalGarmentPicker';
import {
  getPrintAreasForGarment,
  pixelsToInches,
  inchesToPixels,
  getPrintTemplatesByCategory,
  PRINT_AREA_TEMPLATES,
  GARMENT_SPECS,
} from '../../config/printAreaConfig';

/**
 * GarmentDesignerPage
 *
 * Production-ready garment design tool inspired by Tapstitch.
 * Replaces MockupBuilderPanel with functional, integrated workflow.
 *
 * Features:
 * - Fabric.js canvas with working controls
 * - Real-time pricing calculations
 * - Upload validation (file type, size, resolution)
 * - Auto-placement in print area
 * - Layer management
 * - Integration with Campaign Builder
 */
const GarmentDesignerPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);

  // DEBUG: Log what we received from Design Studio
  console.log('🔍 DEBUG: GarmentDesignerPage received location.state:', location.state);

  // Product context from Design Studio or Campaign Builder
  const productContext = location.state?.product || {
    id: 'demo-product',
    name: 'Sample Black Polo Shirt',
    categoryLabel: 'polo',
    basePrice: 12.09,
    garmentColor: 'black',
    technique: 'DTF',
    printAreas: [
      {
        name: 'Front',
        x: 100,
        y: 150,
        width: 400,
        height: 400,
        printCost: 2.00
      },
      {
        name: 'Back',
        x: 100,
        y: 150,
        width: 400,
        height: 500,
        printCost: 2.00
      }
    ],
    mockupImages: {
      front: null, // Will use placeholder color
      back: null
    }
  };

  console.log('🔍 DEBUG: productContext being used:', productContext);
  console.log('🔍 DEBUG: mockupImages:', productContext.mockupImages);

  const returnPath = location.state?.returnPath || '/campaigns';
  const existingDesign = location.state?.existingDesign || null;
  const returnState = location.state?.returnState || {};
  const brandId = returnState.brandId || location.state?.brandId;
  const lookId = returnState.activeLookId || location.state?.lookId;
  const bimesterId = returnState.selectedBimesterId || location.state?.bimesterId;

  // Get product info from dropConfig if available
  const dropConfig = location.state?.dropConfig;
  const productInfo = dropConfig?.productInfo || {};
  const productName = productInfo.name || productContext.name || '';
  const productSku = productInfo.sku || '';

  console.log('📝 Product Info for Save Title:', {
    productName,
    productSku,
    hasDropConfig: !!dropConfig,
    hasProductInfo: !!productInfo,
  });

  // Canvas state
  const [fabricCanvas, setFabricCanvas] = useState(null);
  const [canvasReady, setCanvasReady] = useState(false);

  // View state
  const [activeView, setActiveView] = useState('front'); // 'front' or 'back'
  const [activePrintArea, setActivePrintArea] = useState(productContext.printAreas[0]);

  // Layer management
  const [layers, setLayers] = useState([]);
  const [selectedLayerId, setSelectedLayerId] = useState(null);

  // Pricing state
  const [pricing, setPricing] = useState({
    baseGarment: productContext.basePrice,
    printCosts: {},
    quantity: 1,
    unitPrice: productContext.basePrice,
    total: productContext.basePrice,
  });

  // Attributes state (for selected layer)
  const [attributes, setAttributes] = useState({
    width: 0,
    height: 0,
    rotation: 0,
    scale: 100,
    x: 0,
    y: 0,
  });

  // Active tool
  const [activeTool, setActiveTool] = useState('upload');

  // Print area lock state
  const [printAreaLocked, setPrintAreaLocked] = useState(true);

  // Print size selection
  const [selectedPrintSize, setSelectedPrintSize] = useState('STANDARD');

  // Garment picker state
  const [garmentPickerOpen, setGarmentPickerOpen] = useState(false);
  const [currentGarment, setCurrentGarment] = useState({
    styleNumber: productContext.styleNumber || null,
    styleName: productContext.styleName || 'Sample Garment',
    color: productContext.garmentColor || 'black',
    mockupImages: productContext.mockupImages || { front: null, back: null },
  });
  const [garmentSize, setGarmentSize] = useState('MED');

  // Save design dialog state
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [designName, setDesignName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [removingBackground, setRemovingBackground] = useState(false);

  // Initialize Fabric.js canvas
  useEffect(() => {
    if (!canvasRef.current || fabricCanvas) return;

    console.log('🎨 Initializing Fabric.js canvas...');

    const canvas = new fabric.Canvas(canvasRef.current, {
      width: 600,
      height: 700,
      backgroundColor: '#f5f5f5',
      selection: true,
      preserveObjectStacking: true,
    });

    // Set default object properties
    fabric.Object.prototype.set({
      borderColor: '#2196f3',
      cornerColor: '#2196f3',
      cornerSize: 10,
      transparentCorners: false,
      borderScaleFactor: 2,
      padding: 5,
    });

    // Add garment background (placeholder for now)
    // Wait for canvas to be fully ready before adding background
    setTimeout(() => {
      if (canvas && canvas.getContext()) {
        addGarmentBackground(canvas, 'front');
      }
    }, 100);

    // Canvas event handlers
    canvas.on('selection:created', handleSelectionCreated);
    canvas.on('selection:updated', handleSelectionUpdated);
    canvas.on('selection:cleared', handleSelectionCleared);
    canvas.on('object:modified', handleObjectModified);
    canvas.on('object:added', handleObjectAdded);
    canvas.on('object:removed', handleObjectRemoved);

    setFabricCanvas(canvas);
    setCanvasReady(true);

    console.log('✅ Canvas initialized successfully');

    return () => {
      canvas.dispose();
    };
  }, []);

  // Redraw print area when it changes, canvas is ready, or lock state changes
  useEffect(() => {
    if (!fabricCanvas || !activePrintArea || !canvasReady) return;

    // Use requestAnimationFrame to ensure canvas context is fully ready
    const timeoutId = setTimeout(() => {
      try {
        // Extra safety check: ensure canvas context is ready
        if (!fabricCanvas.getContext()) {
          console.warn('Canvas context not ready yet, skipping print area boundary');
          return;
        }
        addPrintAreaBoundary(fabricCanvas, activePrintArea);
        fabricCanvas.renderAll();
      } catch (error) {
        console.error('Error adding print area boundary:', error);
      }
    }, 100); // 100ms delay to ensure canvas context is initialized

    return () => clearTimeout(timeoutId);
  }, [fabricCanvas, activePrintArea, canvasReady, printAreaLocked]); // Added printAreaLocked dependency

  // Redraw garment background when currentGarment changes
  useEffect(() => {
    if (!fabricCanvas || !canvasReady) return;

    console.log('🔄 Garment changed, redrawing background...');

    // Remove old background
    const objects = fabricCanvas.getObjects();
    const oldBackground = objects.find(obj => obj.name === 'garment-background');
    if (oldBackground) {
      fabricCanvas.remove(oldBackground);
    }

    // Add new background
    setTimeout(() => {
      if (fabricCanvas && fabricCanvas.getContext()) {
        addGarmentBackground(fabricCanvas, activeView);
        fabricCanvas.renderAll();
      }
    }, 100);
  }, [currentGarment, fabricCanvas, canvasReady, activeView]);

  // Handle garment selection from picker
  const handleGarmentSelect = (garment) => {
    console.log('✅ Selected garment:', garment);
    setCurrentGarment(garment);

    // Update print areas based on garment specs and current size
    const newPrintAreas = getPrintAreasForGarment(garment.styleNumber, garmentSize, activeView);
    if (newPrintAreas && newPrintAreas.length > 0) {
      setActivePrintArea(newPrintAreas[0]);
      console.log('📐 Print area updated to real-world dimensions:', newPrintAreas[0]);
    }

    toast.success(`Selected ${garment.styleName || `Style ${garment.styleNumber}`} - ${garment.color}`);
  };

  // Handle garment size change
  const handleGarmentSizeChange = (event) => {
    const newSize = event.target.value;
    setGarmentSize(newSize);

    // Update print areas based on new size
    const newPrintAreas = getPrintAreasForGarment(currentGarment.styleNumber, newSize, activeView);
    if (newPrintAreas && newPrintAreas.length > 0) {
      setActivePrintArea(newPrintAreas[0]);

      // Update the print area boundary on canvas
      if (fabricCanvas) {
        addPrintAreaBoundary(fabricCanvas, newPrintAreas[0]);
      }

      console.log(`📐 Print area updated for size ${newSize}:`, newPrintAreas[0]);
      toast.success(`Print area adjusted for size ${newSize}`);
    }
  };

  // Handle print size change
  const handlePrintSizeChange = (event) => {
    const templateId = event.target.value;
    setSelectedPrintSize(templateId);

    const template = PRINT_AREA_TEMPLATES[templateId];
    if (!template) return;

    // Update print area with new dimensions
    const newPrintArea = {
      ...activePrintArea,
      width: inchesToPixels(template.widthInches),
      height: inchesToPixels(template.heightInches),
      widthInches: template.widthInches,
      heightInches: template.heightInches,
      name: template.name,
      description: template.description,
    };

    setActivePrintArea(newPrintArea);
    console.log(`📐 Print size changed to: ${template.description}`);
    toast.success(`Print size: ${template.description}`);
  };

  // Add garment background (mockup image or placeholder)
  const addGarmentBackground = (canvas, view = 'front') => {
    // Safety check
    if (!canvas) {
      console.warn('addGarmentBackground: canvas is null');
      return;
    }

    const mockupUrl = view === 'front'
      ? currentGarment.mockupImages?.front
      : currentGarment.mockupImages?.back;

    console.log('🖼️ addGarmentBackground called:', {
      view,
      mockupUrl,
      currentGarment
    });

    // If we have a mockup image from supplier, use it
    if (mockupUrl) {
      console.log('✅ Loading mockup from:', mockupUrl);
      fabric.Image.fromURL(mockupUrl, (img) => {
        if (!img || !img.width) {
          console.error('❌ Failed to load mockup image:', mockupUrl);
          return;
        }

        console.log('✅ Mockup image loaded successfully!', img.width, 'x', img.height);

        img.set({
          left: 0,
          top: 0,
          selectable: false,
          evented: false,
          name: 'garment-background',
        });

        // Scale to fit canvas
        const scaleX = 600 / img.width;
        const scaleY = 700 / img.height;
        const scale = Math.min(scaleX, scaleY);
        img.scale(scale);

        canvas.add(img);
        canvas.sendToBack(img);
        canvas.renderAll();
        console.log('🎨 Garment added to canvas');
      }, {
        crossOrigin: 'anonymous'
      });
    } else {
      // Fallback: Try backup garment placeholder image first
      const backupImageUrl = '/Image_Assets/backup-garment-placeholder.png';

      fabric.Image.fromURL(backupImageUrl, (img) => {
        if (img && img.width && img.width > 0) {
          // Image loaded successfully
          img.set({
            left: 0,
            top: 0,
            selectable: false,
            evented: false,
            name: 'garment-background',
          });

          // Scale to fit canvas
          const scaleX = 600 / img.width;
          const scaleY = 700 / img.height;
          const scale = Math.min(scaleX, scaleY);
          img.scale(scale);

          canvas.add(img);
          canvas.sendToBack(img);
          canvas.renderAll();
        } else {
          // If backup image fails, use solid color (graceful fallback)
          console.log('Backup image not found, using solid color background');
          const rect = new fabric.Rect({
            left: 0,
            top: 0,
            width: 600,
            height: 700,
            fill: '#2c2c2c', // Dark gray for garment
            selectable: false,
            evented: false,
            name: 'garment-background',
          });
          canvas.add(rect);
          canvas.sendToBack(rect);
          canvas.renderAll();
        }
      }, {
        crossOrigin: 'anonymous'
      });
    }
  };

  // Add print area boundary (dashed box)
  const addPrintAreaBoundary = (canvas, printArea) => {
    if (!canvas || !printArea) {
      console.warn('addPrintAreaBoundary: canvas or printArea is missing');
      return;
    }

    // Safety check: ensure canvas context exists
    try {
      if (!canvas.getContext()) {
        console.warn('addPrintAreaBoundary: canvas context not ready');
        return;
      }
    } catch (error) {
      console.error('addPrintAreaBoundary: error checking canvas context:', error);
      return;
    }

    // Remove existing boundary and label
    canvas.getObjects().forEach((obj) => {
      if (obj.name === 'print-area-boundary' || obj.name === 'print-area-label') {
        canvas.remove(obj);
      }
    });

    const boundary = new fabric.Rect({
      left: printArea.x,
      top: printArea.y,
      width: printArea.width,
      height: printArea.height,
      fill: 'transparent',
      stroke: printAreaLocked ? '#999' : '#2196f3', // Blue when unlocked
      strokeWidth: printAreaLocked ? 2 : 3,
      strokeDashArray: [10, 5],
      selectable: !printAreaLocked, // Can select/move when unlocked
      evented: !printAreaLocked,    // Can interact when unlocked
      hasControls: !printAreaLocked, // Show resize handles when unlocked
      hasBorders: !printAreaLocked,  // Show borders when unlocked
      lockRotation: true, // Never allow rotation
      name: 'print-area-boundary',
    });

    // Listen for modifications to update activePrintArea
    if (!printAreaLocked) {
      boundary.on('modified', () => {
        setActivePrintArea({
          ...activePrintArea,
          x: Math.round(boundary.left),
          y: Math.round(boundary.top),
          width: Math.round(boundary.width * boundary.scaleX),
          height: Math.round(boundary.height * boundary.scaleY),
        });
      });
    }

    canvas.add(boundary);
    boundary.moveTo(1); // Above background, below designs

    // Add dimension label
    const widthInches = printArea.widthInches || pixelsToInches(printArea.width);
    const heightInches = printArea.heightInches || pixelsToInches(printArea.height);

    const label = new fabric.Text(`${widthInches}" × ${heightInches}"`, {
      left: printArea.x + printArea.width / 2,
      top: printArea.y - 20,
      fontSize: 14,
      fill: printAreaLocked ? '#666' : '#2196f3',
      fontFamily: 'Arial',
      textAlign: 'center',
      originX: 'center',
      selectable: false,
      evented: false,
      name: 'print-area-label',
    });

    canvas.add(label);
    canvas.renderAll();
  };

  // Selection event handlers
  const handleSelectionCreated = (e) => {
    const obj = e.selected[0];
    updateAttributesFromObject(obj);
    setSelectedLayerId(obj.id);
  };

  const handleSelectionUpdated = (e) => {
    const obj = e.selected[0];
    updateAttributesFromObject(obj);
    setSelectedLayerId(obj.id);
  };

  const handleSelectionCleared = () => {
    setSelectedLayerId(null);
    setAttributes({
      width: 0,
      height: 0,
      rotation: 0,
      scale: 100,
      x: 0,
      y: 0,
    });
  };

  const handleObjectModified = (e) => {
    const obj = e.target;
    updateAttributesFromObject(obj);
    updatePricing();
  };

  const handleObjectAdded = () => {
    updatePricing();
  };

  const handleObjectRemoved = () => {
    updatePricing();
  };

  // Update attributes panel from selected object
  const updateAttributesFromObject = (obj) => {
    if (!obj) return;

    setAttributes({
      width: Math.round(obj.width * obj.scaleX),
      height: Math.round(obj.height * obj.scaleY),
      rotation: Math.round(obj.angle),
      scale: Math.round(obj.scaleX * 100),
      x: Math.round(obj.left),
      y: Math.round(obj.top),
    });
  };

  // Handle file upload
  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      // Validate file
      await validateUpload(file);

      // Create image URL
      const imageUrl = URL.createObjectURL(file);

      // Add to canvas
      addImageToCanvas(imageUrl, file.name);

      // Clear input
      e.target.value = '';

      toast.success('Image uploaded successfully!');
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Validate upload
  const validateUpload = async (file) => {
    // File type
    const validTypes = ['image/jpeg', 'image/png'];
    if (!validTypes.includes(file.type)) {
      throw new Error('Only JPG and PNG files are supported');
    }

    // File size (50MB max)
    if (file.size > 50 * 1024 * 1024) {
      throw new Error('File must be under 50MB');
    }

    // Resolution check
    const dimensions = await getImageDimensions(file);
    if (dimensions.width < 1024 || dimensions.height < 1024) {
      throw new Error(
        `Image must be at least 1024×1024px. Current: ${dimensions.width}×${dimensions.height}px`
      );
    }

    return true;
  };

  // Get image dimensions
  const getImageDimensions = (file) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);

      img.onload = () => {
        resolve({ width: img.width, height: img.height });
        URL.revokeObjectURL(url);
      };

      img.onerror = () => {
        reject(new Error('Failed to load image'));
        URL.revokeObjectURL(url);
      };

      img.src = url;
    });
  };

  // Add image to canvas
  const addImageToCanvas = (imageUrl, imageName) => {
    if (!fabricCanvas) return;

    fabric.Image.fromURL(imageUrl, (img) => {
      // Scale to fit 70% of print area
      const targetWidth = activePrintArea.width * 0.7;
      const scale = targetWidth / img.width;

      // Center in print area
      const left = activePrintArea.x + (activePrintArea.width - targetWidth) / 2;
      const top = activePrintArea.y + (activePrintArea.height - img.height * scale) / 2;

      const layerId = `layer-${Date.now()}`;

      img.set({
        left,
        top,
        scaleX: scale,
        scaleY: scale,
        selectable: true,
        hasControls: true,
        hasBorders: true,
        id: layerId,
        name: imageName || 'Design',
      });

      fabricCanvas.add(img);
      fabricCanvas.setActiveObject(img);
      fabricCanvas.renderAll();

      // Add to layers list
      const newLayer = {
        id: layerId,
        name: imageName || 'Design',
        url: imageUrl,
        fabricObject: img,
        visible: true,
        locked: false,
      };

      setLayers((prev) => [...prev, newLayer]);

      console.log('✅ Image added to canvas:', layerId);
    });
  };

  // Update pricing
  const updatePricing = () => {
    if (!fabricCanvas) return;

    // Check which print areas have designs
    const printAreasWithDesigns = new Set();

    fabricCanvas.getObjects().forEach((obj) => {
      if (obj.type === 'image' && obj.name !== 'garment-background') {
        // Check if design is in current print area
        const objBounds = obj.getBoundingRect();
        const isInPrintArea =
          objBounds.left >= activePrintArea.x &&
          objBounds.top >= activePrintArea.y &&
          objBounds.left + objBounds.width <= activePrintArea.x + activePrintArea.width &&
          objBounds.top + objBounds.height <= activePrintArea.y + activePrintArea.height;

        if (isInPrintArea) {
          printAreasWithDesigns.add(activeView);
        }
      }
    });

    // Calculate print costs
    const printCosts = {};
    let totalPrintCost = 0;

    productContext.printAreas.forEach((area) => {
      const viewName = area.name.toLowerCase();
      if (printAreasWithDesigns.has(viewName)) {
        printCosts[area.name] = area.printCost;
        totalPrintCost += area.printCost;
      }
    });

    // Calculate totals
    const unitPrice = pricing.baseGarment + totalPrintCost;
    const total = unitPrice * pricing.quantity;

    setPricing((prev) => ({
      ...prev,
      printCosts,
      unitPrice,
      total,
    }));
  };

  // Toggle layer visibility
  const toggleLayerVisibility = (layerId) => {
    const layer = layers.find((l) => l.id === layerId);
    if (!layer) return;

    layer.fabricObject.visible = !layer.fabricObject.visible;
    layer.visible = !layer.visible;

    fabricCanvas.renderAll();
    setLayers([...layers]);
  };

  // Toggle layer lock
  const toggleLayerLock = (layerId) => {
    const layer = layers.find((l) => l.id === layerId);
    if (!layer) return;

    layer.fabricObject.selectable = layer.locked;
    layer.fabricObject.evented = layer.locked;
    layer.locked = !layer.locked;

    fabricCanvas.renderAll();
    setLayers([...layers]);
  };

  // Delete layer
  const deleteLayer = (layerId) => {
    const layer = layers.find((l) => l.id === layerId);
    if (!layer) return;

    fabricCanvas.remove(layer.fabricObject);
    setLayers(layers.filter((l) => l.id !== layerId));
    toast.success('Layer deleted');
  };

  // Handle view change (front/back)
  const handleViewChange = (event, newView) => {
    if (!newView || !fabricCanvas) return;

    setActiveView(newView);

    // Update garment background for new view (front/back)
    // Remove old background
    const objects = fabricCanvas.getObjects();
    const oldBackground = objects.find(obj => obj.name === 'garment-background');
    if (oldBackground) {
      fabricCanvas.remove(oldBackground);
    }

    // Add new background for the view
    // Pass the new view directly instead of waiting for state update
    setTimeout(() => {
      if (fabricCanvas && fabricCanvas.getContext()) {
        addGarmentBackground(fabricCanvas, newView);
        fabricCanvas.renderAll();
      }
    }, 50);

    // Update print area based on garment specs, size, and new view
    const newPrintAreas = getPrintAreasForGarment(currentGarment.styleNumber, garmentSize, newView);
    if (newPrintAreas && newPrintAreas.length > 0) {
      setActivePrintArea(newPrintAreas[0]);
      addPrintAreaBoundary(fabricCanvas, newPrintAreas[0]);
      console.log(`📐 Print area updated for ${newView} view (size: ${garmentSize}):`, newPrintAreas[0]);
    }
  };

  // Remove background from selected layer
  const handleRemoveBackground = async (layerId) => {
    if (!fabricCanvas) {
      toast.error('Canvas not ready');
      return;
    }

    const layer = layers.find((l) => l.id === layerId);
    if (!layer) {
      toast.error('Layer not found');
      return;
    }

    setRemovingBackground(true);
    toast.loading('Removing background...', { id: 'remove-bg' });

    try {
      const response = await axios.post('/api/remove-background', {
        imageUrl: layer.url,
      });

      if (response.data.success && response.data.imageUrl) {
        const newImageUrl = response.data.imageUrl;

        // Find the fabric object for this layer
        const fabricObjects = fabricCanvas.getObjects();
        const obj = fabricObjects.find((o) => o.id === layerId);

        if (obj && obj.type === 'image') {
          // Load new image with background removed
          fabric.Image.fromURL(
            newImageUrl,
            (fabricImg) => {
              // Preserve all the current properties
              fabricImg.set({
                id: obj.id,
                name: obj.name,
                left: obj.left,
                top: obj.top,
                scaleX: obj.scaleX,
                scaleY: obj.scaleY,
                angle: obj.angle,
                opacity: obj.opacity,
                selectable: obj.selectable,
                hasControls: obj.hasControls,
                hasBorders: obj.hasBorders,
                lockMovementX: obj.lockMovementX,
                lockMovementY: obj.lockMovementY,
              });

              // Remove old object and add new one
              fabricCanvas.remove(obj);
              fabricCanvas.add(fabricImg);
              fabricCanvas.setActiveObject(fabricImg);
              fabricCanvas.renderAll();

              // Update layer URL in state
              setLayers((prevLayers) =>
                prevLayers.map((l) =>
                  l.id === layerId ? { ...l, url: newImageUrl } : l
                )
              );

              toast.success('Background removed!', { id: 'remove-bg' });
            },
            { crossOrigin: 'anonymous' }
          );
        } else {
          toast.error('Could not find layer on canvas', { id: 'remove-bg' });
        }
      } else {
        throw new Error(response.data.message || 'Background removal failed');
      }
    } catch (error) {
      console.error('Background removal error:', error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        'Failed to remove background';

      if (error.response?.data?.needsSetup) {
        toast.error(
          'Background removal not configured. Please add REMOVE_BG_API_KEY to your .env file.',
          { id: 'remove-bg', duration: 5000 }
        );
      } else {
        toast.error(errorMessage, { id: 'remove-bg' });
      }
    } finally {
      setRemovingBackground(false);
    }
  };

  // Open save dialog
  const handleSave = () => {
    if (!fabricCanvas || layers.length === 0) {
      toast.error('Please add at least one design element');
      return;
    }

    // Generate a default name using product name and SKU
    let defaultName = '';

    if (productName && productSku) {
      // Both name and SKU available: "Product Name - SKU"
      defaultName = `${productName} - ${productSku}`;
    } else if (productName) {
      // Only name available: "Product Name - Date"
      defaultName = `${productName} - ${new Date().toLocaleDateString()}`;
    } else if (productSku) {
      // Only SKU available: "SKU - Garment - Color"
      defaultName = `${productSku} - ${currentGarment.styleName || 'Design'} - ${currentGarment.color}`;
    } else {
      // Fallback to garment info
      defaultName = `${currentGarment.styleName || 'Design'} - ${currentGarment.color} - ${new Date().toLocaleDateString()}`;
    }

    setDesignName(defaultName);
    setSaveDialogOpen(true);
  };

  // Actually save the design to database
  const handleConfirmSave = async () => {
    if (!designName.trim()) {
      toast.error('Please enter a design name');
      return;
    }

    if (!brandId || !lookId) {
      toast.error('Missing brand or look information. Cannot save design.');
      return;
    }

    setIsSaving(true);

    try {
      // Generate thumbnail (optional - canvas as base64 image)
      const thumbnail = fabricCanvas.toDataURL({
        format: 'png',
        quality: 0.7,
        multiplier: 0.3, // Smaller thumbnail
      });

      const designData = {
        brandId,
        lookId,
        bimesterId,
        name: designName.trim(),
        productId: productContext.id,
        productName: productContext.name,
        styleNumber: currentGarment.styleNumber,
        styleName: currentGarment.styleName,
        garmentColor: currentGarment.color,
        technique: productContext.technique,

        // Canvas state (full Fabric.js JSON for reconstruction)
        canvasJSON: fabricCanvas.toJSON(['id', 'name', 'selectable']),

        // Layers for easier access
        layers: layers.map((layer) => ({
          id: layer.id,
          name: layer.name,
          url: layer.url,
          visible: layer.visible,
          locked: layer.locked,
        })),

        // Print areas used
        printAreasUsed: Object.keys(pricing.printCosts),

        // Pricing snapshot
        pricing: {
          baseGarment: pricing.baseGarment,
          printCosts: pricing.printCosts,
          unitPrice: pricing.unitPrice,
          total: pricing.total,
          quantity: pricing.quantity,
        },

        // Thumbnail preview
        thumbnail,
      };

      console.log('💾 Saving design to database:', designData);

      const response = await axios.post('/api/garment-designs', designData);

      if (response.data.success) {
        // Also update the look's productInfo with the design name
        // Parse the design name to extract product name and SKU
        // Format is expected to be: "Product Name - SKU" or just "Product Name"
        const nameParts = designName.trim().split(' - ');
        const parsedProductName = nameParts[0] || '';
        const parsedSku = nameParts[1] || '';

        // Update the look's productInfo in the backend
        if (brandId && lookId && parsedProductName) {
          try {
            await axios.patch(`/api/brands/${brandId}/looks/${lookId}/product-info`, {
              name: parsedProductName,
              sku: parsedSku
            });
            console.log('✅ Updated look productInfo:', { name: parsedProductName, sku: parsedSku });
          } catch (updateError) {
            console.error('Failed to update look productInfo:', updateError);
            // Don't fail the whole save if this update fails
          }
        }

        // Add design to look's assets library
        if (brandId && lookId && thumbnail) {
          try {
            const assetData = {
              id: response.data.designId,
              name: designName.trim(),
              category: 'garment-design',
              sourceType: 'garment-designer',
              url: thumbnail, // Use the thumbnail as the asset preview
              lookId: lookId,
              meta: {
                designId: response.data.designId,
                garmentColor: currentGarment.color,
                styleNumber: currentGarment.styleNumber,
                styleName: currentGarment.styleName,
                printAreasUsed: Object.keys(pricing.printCosts),
              },
              createdAt: new Date(),
              updatedAt: new Date()
            };

            // Fetch current drop config to get existing assets
            const dropConfigResponse = await axios.get(`/api/design-studio/brands/${brandId}/drop-config/${lookId}`);
            const currentConfig = dropConfigResponse.data || {};
            const currentAssets = currentConfig.assets?.library || [];

            // Add new asset to library
            const updatedAssets = [...currentAssets, assetData];

            // Save updated config with new asset
            await axios.post(`/api/design-studio/brands/${brandId}/drop-config/${lookId}`, {
              ...currentConfig,
              assets: {
                ...currentConfig.assets,
                library: updatedAssets
              }
            });

            console.log('✅ Added design to assets library');
          } catch (assetError) {
            console.error('Failed to add design to assets:', assetError);
            // Don't fail the whole save if this update fails
          }
        }

        toast.success('Design saved successfully!');
        setSaveDialogOpen(false);
        setDesignName('');

        // Navigate back with success indicator
        navigate(returnPath, {
          state: {
            fromDesigner: true,
            designSaved: true,
            designId: response.data.designId,
            ...returnState,
          },
        });
      } else {
        throw new Error(response.data.message || 'Failed to save design');
      }
    } catch (error) {
      console.error('Error saving design:', error);
      toast.error(error.response?.data?.message || error.message || 'Failed to save design');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle back navigation
  const handleBack = () => {
    if (layers.length > 0) {
      if (window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
        navigate(returnPath);
      }
    } else {
      navigate(returnPath);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      {/* Top Bar */}
      <Paper
        elevation={1}
        sx={{
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderRadius: 0,
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <IconButton onClick={handleBack}>
            <BackIcon />
          </IconButton>
          <Box>
            <Typography variant="h6">{productContext.name}</Typography>
            <Typography variant="caption" color="text.secondary">
              {productContext.technique} • {productContext.categoryLabel}
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="h5" fontWeight={600} color="primary">
            ${pricing.total.toFixed(2)}
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={layers.length === 0}
          >
            Save Design
          </Button>
        </Stack>
      </Paper>

      {/* Main Content */}
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Left Toolbar */}
        <Paper
          elevation={2}
          sx={{
            width: 280,
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            overflow: 'auto',
          }}
        >
          <Typography variant="subtitle1" fontWeight={600}>
            Tools
          </Typography>

          {/* Change Garment Button */}
          <Card
            variant="outlined"
            sx={{
              cursor: 'pointer',
              '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
            }}
            onClick={() => setGarmentPickerOpen(true)}
          >
            <CardContent sx={{ textAlign: 'center', py: 3 }}>
              <ProductIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
              <Typography variant="body1" fontWeight={600}>
                Change Garment
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {currentGarment.styleName} • {currentGarment.color}
              </Typography>
            </CardContent>
          </Card>

          {/* Garment Size Selector */}
          <FormControl fullWidth size="small">
            <InputLabel>Garment Size</InputLabel>
            <Select
              value={garmentSize}
              label="Garment Size"
              onChange={handleGarmentSizeChange}
            >
              {currentGarment.styleNumber && GARMENT_SPECS[currentGarment.styleNumber] ? (
                Object.keys(GARMENT_SPECS[currentGarment.styleNumber].sizes).map((size) => (
                  <MenuItem key={size} value={size}>
                    {size}
                  </MenuItem>
                ))
              ) : (
                // Default sizes if no garment specs available
                <>
                  <MenuItem value="SML">SML</MenuItem>
                  <MenuItem value="MED">MED</MenuItem>
                  <MenuItem value="LRG">LRG</MenuItem>
                  <MenuItem value="XLG">XLG</MenuItem>
                  <MenuItem value="2XL">2XL</MenuItem>
                  <MenuItem value="3XL">3XL</MenuItem>
                </>
              )}
            </Select>
          </FormControl>

          {/* Upload Button */}
          <Card
            variant="outlined"
            sx={{
              cursor: 'pointer',
              '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
            }}
            onClick={() => fileInputRef.current?.click()}
          >
            <CardContent sx={{ textAlign: 'center', py: 3 }}>
              <UploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
              <Typography variant="body1" fontWeight={600}>
                Upload Image
              </Typography>
              <Typography variant="caption" color="text.secondary">
                JPG/PNG • Max 50MB
              </Typography>
            </CardContent>
          </Card>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png"
            style={{ display: 'none' }}
            onChange={handleFileSelect}
          />

          <Divider />

          {/* Upload Requirements */}
          <Alert severity="info">
            <Typography variant="caption">
              <strong>Image Requirements:</strong>
              <br />
              • Min 1024×1024px
              <br />
              • JPG or PNG format
              <br />• Max 50MB file size
            </Typography>
          </Alert>
        </Paper>

        {/* Center Canvas */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'grey.100',
            p: 3,
            position: 'relative',
          }}
        >
          {/* View Toggle, Print Size, and Print Area Lock - Floating above canvas */}
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{
              position: 'absolute',
              top: 16,
              left: '50%',
              transform: 'translateX(-50%)',
              bgcolor: 'primary.main',
              px: 2,
              py: 1,
              borderRadius: 2,
              boxShadow: 3,
              zIndex: 10,
            }}
          >
            <ToggleButtonGroup
              value={activeView}
              exclusive
              onChange={handleViewChange}
              size="small"
              sx={{
                '& .MuiToggleButton-root': {
                  color: '#fff',
                  borderColor: 'rgba(255,255,255,0.3)',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.1)',
                  },
                  '&.Mui-selected': {
                    bgcolor: 'rgba(255,255,255,0.2)',
                    color: '#fff',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.25)',
                    },
                  },
                },
              }}
            >
              <ToggleButton value="front">Front</ToggleButton>
              <ToggleButton value="back">Back</ToggleButton>
            </ToggleButtonGroup>

            {/* Print Size Selector */}
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <Select
                value={selectedPrintSize}
                onChange={handlePrintSizeChange}
                sx={{
                  color: '#fff',
                  '.MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255,255,255,0.3)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255,255,255,0.5)',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#fff',
                  },
                  '.MuiSvgIcon-root': {
                    color: '#fff',
                  },
                }}
              >
                {getPrintTemplatesByCategory(activeView === 'front' ? 'front' : 'back').map((template) => (
                  <MenuItem key={template.id} value={template.id}>
                    {template.name} ({template.widthInches}" × {template.heightInches}")
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Tooltip title={printAreaLocked ? "Unlock to adjust print area size and position" : "Lock print area"}>
              <IconButton
                onClick={() => setPrintAreaLocked(!printAreaLocked)}
                size="small"
                sx={{
                  border: 1,
                  borderColor: 'rgba(255,255,255,0.5)',
                  color: '#fff',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.1)',
                  },
                }}
              >
                {printAreaLocked ? <LockIcon /> : <LockOpenIcon />}
              </IconButton>
            </Tooltip>

            {!printAreaLocked && (
              <Typography variant="caption" sx={{ color: '#fff' }}>
                Drag and resize the dotted box
              </Typography>
            )}
          </Stack>

          {/* Canvas - no wrapper */}
          <canvas
            ref={canvasRef}
            style={{
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
              borderRadius: '4px',
            }}
          />
        </Box>

        {/* Right Panel */}
        <Paper
          elevation={2}
          sx={{
            width: 320,
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            overflow: 'auto',
          }}
        >
          {/* Pricing Section */}
          <Box>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Pricing
            </Typography>

            <Stack spacing={1}>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2">Blank garment</Typography>
                <Typography variant="body2">${pricing.baseGarment.toFixed(2)}</Typography>
              </Box>

              {Object.entries(pricing.printCosts).map(([area, cost]) => (
                <Box key={area} display="flex" justifyContent="space-between">
                  <Typography variant="body2">+ Print {area}</Typography>
                  <Typography variant="body2">${cost.toFixed(2)}</Typography>
                </Box>
              ))}

              <Divider />

              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" fontWeight={600}>
                  Total Price
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  ${pricing.unitPrice.toFixed(2)}
                </Typography>
              </Box>

              <TextField
                label="Quantity"
                type="number"
                size="small"
                value={pricing.quantity}
                onChange={(e) =>
                  setPricing((prev) => ({
                    ...prev,
                    quantity: Math.max(1, parseInt(e.target.value) || 1),
                    total: prev.unitPrice * Math.max(1, parseInt(e.target.value) || 1),
                  }))
                }
                inputProps={{ min: 1 }}
              />

              <Divider />

              <Box display="flex" justifyContent="space-between">
                <Typography variant="h6">Order Total</Typography>
                <Typography variant="h6" color="primary">
                  ${pricing.total.toFixed(2)}
                </Typography>
              </Box>
            </Stack>
          </Box>

          <Divider />

          {/* Attributes Section */}
          <Box>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Attributes
            </Typography>

            {selectedLayerId ? (
              <Stack spacing={2}>
                <Stack direction="row" spacing={2}>
                  <TextField
                    label="Width"
                    type="number"
                    size="small"
                    value={attributes.width}
                    disabled
                    fullWidth
                  />
                  <TextField
                    label="Height"
                    type="number"
                    size="small"
                    value={attributes.height}
                    disabled
                    fullWidth
                  />
                </Stack>

                <Stack direction="row" spacing={2}>
                  <TextField
                    label="Rotate"
                    type="number"
                    size="small"
                    value={attributes.rotation}
                    disabled
                    fullWidth
                  />
                  <TextField
                    label="Scale"
                    type="number"
                    size="small"
                    value={attributes.scale}
                    disabled
                    fullWidth
                  />
                </Stack>
              </Stack>
            ) : (
              <Alert severity="info">Select a layer to view attributes</Alert>
            )}
          </Box>

          <Divider />

          {/* Layers Section */}
          <Box>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Layers
            </Typography>

            {layers.length === 0 ? (
              <Alert severity="info">No layers yet. Upload an image to start.</Alert>
            ) : (
              <Stack spacing={1}>
                {layers.map((layer) => (
                  <Card
                    key={layer.id}
                    variant="outlined"
                    sx={{
                      borderColor: selectedLayerId === layer.id ? 'primary.main' : 'divider',
                      bgcolor: selectedLayerId === layer.id ? 'primary.50' : 'transparent',
                    }}
                  >
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            backgroundImage: `url(${layer.url})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                          }}
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" noWrap fontWeight={600}>
                            {layer.name}
                          </Typography>
                        </Box>
                        <Stack direction="row" spacing={0.5}>
                          <Tooltip title={layer.visible ? 'Hide' : 'Show'}>
                            <IconButton size="small" onClick={() => toggleLayerVisibility(layer.id)}>
                              {layer.visible ? (
                                <VisibilityIcon fontSize="small" />
                              ) : (
                                <VisibilityOffIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={layer.locked ? 'Unlock' : 'Lock'}>
                            <IconButton size="small" onClick={() => toggleLayerLock(layer.id)}>
                              {layer.locked ? (
                                <LockIcon fontSize="small" />
                              ) : (
                                <LockOpenIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Remove Background">
                            <IconButton
                              size="small"
                              color="secondary"
                              onClick={() => handleRemoveBackground(layer.id)}
                              disabled={removingBackground}
                            >
                              {removingBackground ? (
                                <CircularProgress size={16} />
                              ) : (
                                <AutoFixHighIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" onClick={() => deleteLayer(layer.id)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            )}
          </Box>
        </Paper>
      </Box>

      {/* Local Garment Picker Modal */}
      <LocalGarmentPicker
        open={garmentPickerOpen}
        onClose={() => setGarmentPickerOpen(false)}
        onSelect={handleGarmentSelect}
      />

      {/* Save Design Dialog */}
      <Dialog
        open={saveDialogOpen}
        onClose={() => !isSaving && setSaveDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Save Design</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Design Name"
            type="text"
            fullWidth
            value={designName}
            onChange={(e) => setDesignName(e.target.value)}
            disabled={isSaving}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !isSaving) {
                handleConfirmSave();
              }
            }}
            helperText="Give this design a memorable name"
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            This design will be saved to <strong>Look {lookId}</strong> and can be loaded in the Mockup Builder.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setSaveDialogOpen(false)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmSave}
            variant="contained"
            disabled={isSaving || !designName.trim()}
            startIcon={isSaving ? <CircularProgress size={20} /> : <SaveIcon />}
          >
            {isSaving ? 'Saving...' : 'Save Design'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GarmentDesignerPage;
